// src/components/calculator/ResultCard.tsx
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Copy, Check, FileText } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from '@/hooks/use-toast';
import type { CalculationResult } from '@/types/calculator';

interface ResultCardProps {
  result: CalculationResult;
}

const ResultCard = ({ result }: ResultCardProps) => {
  const [copyStatus, setCopyStatus] = useState<{[key: string]: boolean}>({});

  const formatResultText = () => `
PayPal Gebühren Berechnung:
Betrag: ${result.baseAmount}
Gebühren: ${result.fees}
Gebührensatz: ${result.rate}
Endbetrag: ${result.finalAmount}
`.trim();

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus({ ...copyStatus, [key]: true });
      toast({
        title: "Kopiert!",
        description: "Die Daten wurden in die Zwischenablage kopiert.",
        duration: 2000
      });
      setTimeout(() => {
        setCopyStatus({ ...copyStatus, [key]: false });
      }, 2000);
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
      toast({
        title: "Fehler beim Kopieren",
        description: "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="p-6 bg-gray-50">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Ergebnis</h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="ml-2"
                onClick={() => handleCopy(formatResultText(), 'all')}
              >
                {copyStatus['all'] ? <Check className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Komplettes Ergebnis kopieren</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-600">PayPal Gebühren</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(result.fees, 'fees')}
                  >
                    {copyStatus['fees'] ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Gebühren kopieren</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-2xl font-bold text-red-600">
            {result.fees}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-red-600">
              Gebührensatz: {result.rate}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(result.rate, 'rate')}
                  >
                    {copyStatus['rate'] ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Gebührensatz kopieren</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="border-t pt-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-600">Betrag</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(result.finalAmount, 'final')}
                  >
                    {copyStatus['final'] ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Endbetrag kopieren</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {result.finalAmount}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ResultCard;